export const AUTH_PROVIDERS = ['google', 'facebook', 'github', 'microsoft'] as const;
export type AuthProviderId = (typeof AUTH_PROVIDERS)[number];

/** Session identity source — OAuth providers or local Neon password account. */
export type AuthIdentityProvider = AuthProviderId | 'local';

export const AUTH_PROVIDER_LABELS_UK: Record<AuthProviderId, string> = {
  google: 'Google',
  facebook: 'Facebook',
  github: 'GitHub',
  microsoft: 'Microsoft',
};

export type AuthUser = {
  id: string;
  email: string | null;
  name: string | null;
  picture: string | null;
  provider: AuthIdentityProvider;
  providers: AuthIdentityProvider[];
};

export type AuthProviderStatus = Record<AuthProviderId, boolean>;

export type AuthStatusResponse = {
  user: AuthUser | null;
  providers: AuthProviderStatus;
  guestAllowed: boolean;
  /** Local email/password login is available (Neon and/or bootstrap). */
  passwordLogin: boolean;
  /** Public self-registration is closed. */
  registrationOpen: boolean;
  /** Social / IdP OAuth login is allowed. */
  oauthAllowed: boolean;
  /** Neon DATABASE_URL is configured. */
  neonConfigured?: boolean;
};

export type UserUiSettings = {
  theme: 'light' | 'dark';
  iconPack: 'ukrop' | 'classic_network' | 'flat_cloud' | 'minimal' | 'outline';
  displayName: string;
  rememberLogin: boolean;
  /** Zoom when opening monitor/editor: fit-to-screen or fixed percent. */
  diagramOpenZoom: DiagramOpenZoom;
};

/** Opening zoom preset for diagram canvas. */
export type DiagramOpenZoom = 'fit' | 10 | 25 | 50 | 75 | 100;

export const DIAGRAM_OPEN_ZOOM_OPTIONS: ReadonlyArray<{ value: DiagramOpenZoom; label: string }> = [
  { value: 'fit', label: 'Відкриття: вмістити' },
  { value: 10, label: 'Відкриття: 10%' },
  { value: 25, label: 'Відкриття: 25%' },
  { value: 50, label: 'Відкриття: 50%' },
  { value: 75, label: 'Відкриття: 75%' },
  { value: 100, label: 'Відкриття: 100%' },
];

const OPEN_ZOOM_VALUES = new Set<DiagramOpenZoom>(['fit', 10, 25, 50, 75, 100]);

export function normalizeDiagramOpenZoom(value: unknown): DiagramOpenZoom {
  if (value === 'fit') return 'fit';
  if (typeof value === 'number' && OPEN_ZOOM_VALUES.has(value as DiagramOpenZoom)) {
    return value as DiagramOpenZoom;
  }
  if (typeof value === 'string') {
    if (value === 'fit') return 'fit';
    const n = Number(value);
    if (OPEN_ZOOM_VALUES.has(n as DiagramOpenZoom)) return n as DiagramOpenZoom;
  }
  return 50;
}

export type UserWorkspacePayload = {
  sites: unknown;
  diagrams: Record<string, { document: unknown; versions: unknown }>;
  drawingMeta: unknown;
  settings?: UserUiSettings;
  deletedSiteIds?: string[];
  updatedAt: string;
};

export function isAuthProviderId(value: string): value is AuthProviderId {
  return (AUTH_PROVIDERS as readonly string[]).includes(value);
}
